import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { tvApi, type WantedEpisode } from "@/api/tv";
import type { GrabRecord } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { SearchInput } from "@/components/ui/Field";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

type RowState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "grabbed"; record: GrabRecord }
  | { kind: "failed"; reason: string };

export function TVWanted() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [searchingAll, setSearchingAll] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [filter, setFilter] = useState("");
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});

  const { data: wanted, isLoading } = useQuery({
    queryKey: ["tv", "wanted"],
    queryFn: () => tvApi.wanted(500),
  });

  const grabMut = useMutation({
    mutationFn: (id: number) => tvApi.searchEpisode(id),
  });

  const lower = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!wanted) return [];
    if (!lower) return wanted;
    return wanted.filter(
      (e) =>
        e.series_title.toLowerCase().includes(lower) ||
        (e.title?.toLowerCase().includes(lower) ?? false),
    );
  }, [wanted, lower]);

  const onGrab = (ep: WantedEpisode) => {
    setRowStates((s) => ({ ...s, [ep.episode_id]: { kind: "searching" } }));
    grabMut.mutate(ep.episode_id, {
      onSuccess: (rec) => {
        setRowStates((s) => ({
          ...s,
          [ep.episode_id]: { kind: "grabbed", record: rec },
        }));
        toast(
          `Grabbed ${rec.release_title} via ${rec.indexer_name ?? "?"} → ${rec.download_client_name ?? "?"}`,
          "success",
        );
        qc.invalidateQueries({ queryKey: ["tv", "wanted"] });
        qc.invalidateQueries({ queryKey: ["grabs"] });
      },
      onError: (err) => {
        const reason = err instanceof Error ? err.message : "Search failed";
        setRowStates((s) => ({
          ...s,
          [ep.episode_id]: { kind: "failed", reason },
        }));
        toast(reason, "error");
      },
    });
  };

  const searchAll = async () => {
    if (!filtered || filtered.length === 0) return;
    if (
      !confirm(
        `Search ${filtered.length} monitored episode${filtered.length === 1 ? "" : "s"} across your indexers? This may take several minutes.`,
      )
    ) {
      return;
    }
    setSearchingAll(true);
    setProgress({ done: 0, total: filtered.length });
    let succeeded = 0;
    let failed = 0;

    const CONCURRENCY = 3;
    const queue = filtered.slice();
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length > 0) {
        const ep = queue.shift();
        if (!ep) return;
        setRowStates((s) => ({
          ...s,
          [ep.episode_id]: { kind: "searching" },
        }));
        try {
          const rec = await tvApi.searchEpisode(ep.episode_id);
          setRowStates((s) => ({
            ...s,
            [ep.episode_id]: { kind: "grabbed", record: rec },
          }));
          succeeded += 1;
        } catch (err) {
          const reason = err instanceof Error ? err.message : "failed";
          setRowStates((s) => ({
            ...s,
            [ep.episode_id]: { kind: "failed", reason },
          }));
          failed += 1;
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    });
    await Promise.all(workers);
    setSearchingAll(false);
    qc.invalidateQueries({ queryKey: ["tv", "wanted"] });
    qc.invalidateQueries({ queryKey: ["series"] });
    qc.invalidateQueries({ queryKey: ["grabs"] });
    toast(
      `Searched ${filtered.length} episode${filtered.length === 1 ? "" : "s"} — ${succeeded} grabbed, ${failed} no match / failed`,
      succeeded > 0 ? "success" : "info",
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Wanted"
        title="Episodes missing files"
        description="Monitored episodes without a copy in your library. Search per row, or batch-search the whole list."
        actions={
          <Button
            onClick={searchAll}
            loading={searchingAll}
            disabled={!filtered || filtered.length === 0}
          >
            {searchingAll
              ? `Searching ${progress.done}/${progress.total}`
              : "Search all"}
          </Button>
        }
      />

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by series or title"
          />
        </div>
        {wanted && (
          <span className="font-mono text-xs text-foreground/55">
            {filter
              ? `${filtered.length} of ${wanted.length}`
              : `${wanted.length} wanted`}
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton.Rows count={8} />
      ) : !wanted || wanted.length === 0 ? (
        <EmptyState
          glyph="antenna"
          title="Nothing wanted"
          description="Every monitored episode has a file. Add more series or unmonitor some."
          action={
            <Link to="/tv">
              <Button variant="secondary">Back to series</Button>
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description={`Nothing matches "${filter}".`}
          action={
            <Button variant="ghost" onClick={() => setFilter("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50">
              <tr className="text-left">
                <th className="w-16 px-3 py-2.5"></th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Series
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Episode
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Title
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Air date
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Status
                </th>
                <th className="w-32 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((ep) => (
                <WantedRow
                  key={ep.episode_id}
                  ep={ep}
                  state={rowStates[ep.episode_id] ?? { kind: "idle" }}
                  onGrab={() => onGrab(ep)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WantedRow({
  ep,
  state,
  onGrab,
}: {
  ep: WantedEpisode;
  state: RowState;
  onGrab: () => void;
}) {
  const dim = state.kind === "grabbed";
  const seCode =
    `S${String(ep.season_number).padStart(2, "0")}` +
    `E${String(ep.episode_number).padStart(2, "0")}`;
  return (
    <tr
      className={cn(
        "group transition hover:bg-surface-2/40",
        dim && "opacity-60",
      )}
    >
      <td className="px-3 py-2">
        {ep.series_poster_url ? (
          <img
            src={ep.series_poster_url}
            alt=""
            className="aspect-[2/3] w-12 rounded border border-line object-cover"
            loading="lazy"
          />
        ) : (
          <div className="aspect-[2/3] w-12 rounded border border-line bg-surface-2/40" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <span className="font-display text-base font-medium tracking-display-tight">
          {ep.series_title}
        </span>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs tabular-nums text-foreground/75">
        {seCode}
      </td>
      <td className="px-3 py-2.5">
        <span className="text-sm">{ep.title ?? "—"}</span>
        {ep.overview && (
          <div
            className="mt-1 line-clamp-2 max-w-xl text-xs leading-relaxed text-foreground/55"
            title={ep.overview}
          >
            {ep.overview}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs text-foreground/65">
        {ep.air_date ?? "—"}
      </td>
      <td className="px-3 py-2.5">
        <RowStatus state={state} />
      </td>
      <td className="space-x-2 px-3 py-2 text-right whitespace-nowrap">
        {state.kind === "grabbed" ? (
          <span className="font-mono text-[10px] text-foreground/45">
            {state.record.indexer_name ?? "?"}
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            loading={state.kind === "searching"}
            onClick={onGrab}
          >
            {state.kind === "failed" ? "Retry" : "Search"}
          </Button>
        )}
      </td>
    </tr>
  );
}

function RowStatus({ state }: { state: RowState }) {
  switch (state.kind) {
    case "idle":
      return (
        <Status variant="chip" tone="accent">
          wanted
        </Status>
      );
    case "searching":
      return (
        <Status variant="chip" tone="info" pulse>
          searching
        </Status>
      );
    case "grabbed":
      return (
        <Status variant="chip" tone="good">
          grabbed
        </Status>
      );
    case "failed":
      return (
        <Status variant="chip" tone="bad">
          {state.reason.length > 22 ? "failed" : state.reason}
        </Status>
      );
  }
}
